import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import {
  createPlan,
  generateFullOppMap,
  getOppGuideTips,
  getOppQuestions,
  getUsageLimits,
  listPlans,
  suggestOppSectionStream,
} from '../utils/apiServices';
import { getJSON, setJSON } from '../utils/errors';
import { PlansSidebar } from '../components/plans';
import { OppEditor } from '../components/oportunidades';
import { PREDEFINED_OPP_SECTIONS } from '../constants/oportunidadesConstants';
import '../stylesheets/plans.css';

const buildSectionTextFromAnswers = (section, answers) => {
  if (!section?.questions?.length) return '';
  return section.questions
    .map((q) => {
      const value = (answers?.[q.id] || '').trim();
      if (!value) return '';
      return `Pregunta: ${q.question}\nRespuesta: ${value}`;
    })
    .filter(Boolean)
    .join('\n\n');
};

const OportunidadesMap = () => {
  const { user } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [title, setTitle] = useState('');
  const [activeSlug, setActiveSlug] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [questionsData, setQuestionsData] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState('');
  const [answers, setAnswers] = useState({});
  const [usageLimits, setUsageLimits] = useState(null);
  const [guide, setGuide] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [reportResult, setReportResult] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const streamingTextRef = useRef('');

  useEffect(() => {
    if (!guide) return;
    const handleKey = (e) => { if (e.key === 'Escape') setGuide(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [guide]);

  useEffect(() => {
    if (user) {
      getOppQuestions().then((data) => {
        setQuestionsData(data);
        const first = data?.sections_with_questions?.[0];
        if (first?.section_id) setActiveSectionId(first.section_id);
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    listPlans().then((data) => {
      const items = data.results || [];
      setPlans(items);
      const savedSlug = getJSON('last_opp_plan_slug', null);
      const exists = items.find((p) => p.slug === savedSlug);
      if (exists) {
        setActiveSlug(savedSlug);
        return;
      }
      if (items.length > 0) setActiveSlug(items[0].slug);
    });
  }, [user]);

  useEffect(() => {
    if (!activeSlug) {
      setAnswers({});
      setReportResult('');
      setUsageLimits(null);
      return;
    }

    setJSON('last_opp_plan_slug', activeSlug);
    const savedAnswers = getJSON(`opp_answers_${activeSlug}`, {});
    const savedSection = getJSON(`opp_last_section_${activeSlug}`, '');
    const savedResult = getJSON(`opp_result_${activeSlug}`, null);
    setAnswers(savedAnswers || {});
    if (savedSection) setActiveSectionId(savedSection);
    setReportResult(savedResult?.opp_map_md || '');
    getUsageLimits(activeSlug).then(setUsageLimits).catch(() => setUsageLimits(null));
  }, [activeSlug]);

  const sections = useMemo(
    () => questionsData?.sections_with_questions || [],
    [questionsData],
  );

  const activeSection = useMemo(
    () => sections.find((s) => s.section_id === activeSectionId) || sections[0] || null,
    [sections, activeSectionId],
  );

  const currentPlan = useMemo(
    () => plans.find((p) => p.slug === activeSlug) || null,
    [plans, activeSlug],
  );

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const plan = await createPlan({ title });
    setPlans((prev) => [plan, ...prev]);
    setActiveSlug(plan.slug);
    setTitle('');
    setSidebarOpen(false);
  };

  const handleSelectPlan = (slug) => {
    setActiveSlug(slug);
    setAi(null);
    setAiError('');
    setSidebarOpen(false);
  };

  const handleSectionChange = (sectionId) => {
    setActiveSectionId(sectionId);
    setAi(null);
    setAiError('');
    if (activeSlug) setJSON(`opp_last_section_${activeSlug}`, sectionId);
  };

  const handleChangeAnswer = (questionId, value) => {
    const next = { ...answers, [questionId]: value };
    setAnswers(next);
    if (activeSlug) setJSON(`opp_answers_${activeSlug}`, next);
  };

  const handleApplyOption = (questionId, optionValue) => {
    if (!optionValue) return;
    handleChangeAnswer(questionId, optionValue);
  };

  const handleSaveDraft = async () => {
    if (!activeSlug) return;
    setSavingDraft(true);
    setJSON(`opp_answers_${activeSlug}`, answers);
    if (activeSection?.section_id) {
      setJSON(`opp_last_section_${activeSlug}`, activeSection.section_id);
    }
    setSavingDraft(false);
  };

  const handleShowGuide = async () => {
    if (!activeSection?.section_title) return;
    const tips = await getOppGuideTips(activeSection.section_title);
    setGuide(tips);
  };

  const handleSuggestSection = async () => {
    if (!activeSlug || !activeSection?.section_title) return;
    const sectionText = buildSectionTextFromAnswers(activeSection, answers);
    const isPredefined = PREDEFINED_OPP_SECTIONS.has(activeSection.section_title);

    streamingTextRef.current = '';
    setAiError('');
    setAiLoading(true);
    setAi(isPredefined
      ? { streaming: false, ai_suggestion: '' }
      : { streaming: true, ai_suggestion: '' }
    );

    await suggestOppSectionStream(
      activeSlug,
      activeSection.section_title,
      sectionText,
      (chunk) => {
        streamingTextRef.current += chunk;
        setAi({
          streaming: !isPredefined,
          ai_suggestion: streamingTextRef.current,
        });
      },
      (fallbackData) => {
        setAi({ ...fallbackData, streaming: false });
        setAiLoading(false);
      },
      () => {
        setAi({
          streaming: false,
          section: activeSection.section_title,
          ai_suggestion: streamingTextRef.current,
        });
        setAiLoading(false);
        if (!isPredefined) {
          getUsageLimits(activeSlug).then(setUsageLimits).catch(() => null);
        }
      },
      (error) => {
        if (error?.response?.status === 429) {
          setAiError(error.response.data?.detail || 'Has alcanzado el límite de sugerencias para esta sección.');
        } else {
          setAiError('No se pudo generar la sugerencia de IA para esta sección.');
        }
        setAi(null);
        setAiLoading(false);
        getUsageLimits(activeSlug).then(setUsageLimits).catch(() => null);
      },
    );
  };

  const handleGenerateReport = async () => {
    if (!activeSlug) return;
    const sectionsPayload = sections.reduce((acc, section) => {
      const text = buildSectionTextFromAnswers(section, answers);
      if (text) acc[section.section_title] = text;
      return acc;
    }, {});

    if (Object.keys(sectionsPayload).length === 0) {
      setAiError('Completa al menos una sección antes de generar el mapa.');
      return;
    }

    setGeneratingReport(true);
    setAiError('');
    try {
      const data = await generateFullOppMap(activeSlug, {
        sections: sectionsPayload,
      });
      setReportResult(data.opp_map_md || '');
      setJSON(`opp_result_${activeSlug}`, data);
    } catch (error) {
      setAiError(error?.response?.data?.error || 'No se pudo generar el mapa de oportunidades.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const calculatePlanProgress = (plan) => {
    const planAnswers = getJSON(`opp_answers_${plan?.slug}`, {});
    if (!sections.length) return 0;
    const completed = sections.filter((section) =>
      section.questions?.some((q) => (planAnswers[q.id] || '').trim().length > 0),
    ).length;
    return Math.round((completed / sections.length) * 100);
  };

  const handleCloseAi = () => {
    setAi(null);
    setAiError('');
  };

  const handleCloseGuide = () => setGuide(null);

  if (!user) return <Navigate to="/login/" replace />;

  return (
    <div className="plans-container">
      <PlansSidebar
        plans={plans}
        activeSlug={activeSlug}
        title={title}
        setTitle={setTitle}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onCreatePlan={handleCreatePlan}
        onSelectPlan={handleSelectPlan}
        calculateProgress={calculatePlanProgress}
      />

      <main className="editor-card">
        <OppEditor
          currentPlan={currentPlan}
          sections={sections}
          activeSection={activeSection}
          activeSectionId={activeSectionId}
          answers={answers}
          savingDraft={savingDraft}
          aiLoading={aiLoading}
          ai={ai}
          aiError={aiError}
          guide={guide}
          usageLimits={usageLimits}
          generatingReport={generatingReport}
          reportResult={reportResult}
          onSectionChange={handleSectionChange}
          onChangeAnswer={handleChangeAnswer}
          onApplyOption={handleApplyOption}
          onSaveDraft={handleSaveDraft}
          onSuggest={handleSuggestSection}
          onGenerateReport={handleGenerateReport}
          onShowGuide={handleShowGuide}
          onCloseGuide={handleCloseGuide}
          onCloseAi={handleCloseAi}
        />
      </main>
    </div>
  );
};

export default OportunidadesMap;
